import React, { useState, useEffect, useCallback } from 'react';
import { toast } from 'sonner';
import {
  CreditCard, RefreshCw, AlertTriangle, CheckCircle, XCircle,
  Clock, Send, ChevronDown, RotateCcw, CalendarPlus
} from 'lucide-react';
import Sidebar from '../components/Sidebar';
import FinancialTabNav from '../components/FinancialTabNav';
import api from '../services/api';

const STATUS_COLORS = {
  paid: { bg: 'bg-green-50', text: 'text-green-700', label: 'Paid' },
  completed: { bg: 'bg-green-50', text: 'text-green-700', label: 'Completed' },
  pending: { bg: 'bg-amber-50', text: 'text-amber-700', label: 'Pending' },
  failed: { bg: 'bg-red-50', text: 'text-red-700', label: 'Failed' },
  expired: { bg: 'bg-gray-50', text: 'text-gray-700', label: 'Expired' },
  refunded: { bg: 'bg-purple-50', text: 'text-purple-700', label: 'Refunded' },
  unknown: { bg: 'bg-gray-50', text: 'text-gray-500', label: 'Unknown' },
};

const formatDate = (d) => {
  if (!d) return '—';
  try { return new Date(d).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric', hour: '2-digit', minute: '2-digit' }); }
  catch { return d; }
};

const AdminFinancialPayments = () => {
  const [tab, setTab] = useState('history');
  const [transactions, setTransactions] = useState([]);
  const [failedCompanies, setFailedCompanies] = useState([]);
  const [totalTx, setTotalTx] = useState(0);
  const [loading, setLoading] = useState(true);
  const [statusFilter, setStatusFilter] = useState('');
  const [actionLoading, setActionLoading] = useState(null);

  const fetchData = useCallback(async () => {
    setLoading(true);
    try {
      if (tab === 'history') {
        const params = statusFilter ? `?status=${statusFilter}&limit=100` : '?limit=100';
        const res = await api.get(`/admin/financial/payments${params}`);
        setTransactions(res.data.transactions || []);
        setTotalTx(res.data.total || 0);
      } else {
        const res = await api.get('/admin/financial/failed-payments');
        setFailedCompanies(res.data.companies || []);
      }
    } catch (err) {
      toast.error('Failed to load payment data');
    } finally {
      setLoading(false);
    }
  }, [tab, statusFilter]);

  useEffect(() => { fetchData(); }, [fetchData]);

  const handleRetry = async (companyId) => {
    setActionLoading(companyId + '_retry');
    try {
      const res = await api.post(`/admin/financial/retry-payment/${companyId}`);
      toast.success(res.data.message);
      fetchData();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Retry failed');
    } finally {
      setActionLoading(null);
    }
  };

  const handleSendReminder = async (companyId) => {
    setActionLoading(companyId + '_remind');
    try {
      const res = await api.post(`/admin/financial/send-reminder/${companyId}`);
      toast.success(res.data.message);
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to send reminder');
    } finally {
      setActionLoading(null);
    }
  };

  const handleExtendGrace = async (companyId) => {
    setActionLoading(companyId + '_extend');
    try {
      const res = await api.post(`/admin/financial/extend-grace/${companyId}`, { days: 7 });
      toast.success(res.data.message);
      fetchData();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to extend grace period');
    } finally {
      setActionLoading(null);
    }
  };

  const PaymentBadge = ({ status }) => {
    const config = STATUS_COLORS[status] || STATUS_COLORS.unknown;
    return (
      <span className={`inline-flex px-2 py-0.5 rounded-full text-xs font-medium ${config.bg} ${config.text}`}>
        {config.label}
      </span>
    );
  };

  return (
    <Sidebar>
      <FinancialTabNav />
      <div className="p-6 md:p-8 bg-gray-50/50 min-h-screen">
        <div className="max-w-7xl mx-auto space-y-4">

          {/* Sub-tabs: History / Failed Queue */}
          <div className="flex items-center gap-4">
            <button
              onClick={() => setTab('history')}
              className={`px-4 py-2 text-sm font-medium rounded-lg transition ${
                tab === 'history' ? 'bg-white border border-gray-200 text-gray-900 shadow-sm' : 'text-gray-500 hover:text-gray-700'
              }`}
            >
              <CreditCard className="w-4 h-4 inline mr-1.5" />
              Payment History
            </button>
            <button
              onClick={() => setTab('failed')}
              className={`px-4 py-2 text-sm font-medium rounded-lg transition ${
                tab === 'failed' ? 'bg-white border border-gray-200 text-gray-900 shadow-sm' : 'text-gray-500 hover:text-gray-700'
              }`}
            >
              <AlertTriangle className="w-4 h-4 inline mr-1.5" />
              Failed Payments
              {failedCompanies.length > 0 && tab !== 'failed' && (
                <span className="ml-1.5 px-1.5 py-0.5 bg-red-500 text-white text-xs rounded-full">{failedCompanies.length}</span>
              )}
            </button>
          </div>

          {/* Payment History Tab */}
          {tab === 'history' && (
            <>
              <div className="flex items-center gap-3">
                <div className="relative">
                  <select
                    value={statusFilter}
                    onChange={(e) => setStatusFilter(e.target.value)}
                    className="pl-3 pr-8 py-2 border border-gray-300 rounded-lg text-sm appearance-none bg-white"
                  >
                    <option value="">All Status</option>
                    <option value="paid">Paid</option>
                    <option value="pending">Pending</option>
                    <option value="failed">Failed</option>
                    <option value="expired">Expired</option>
                  </select>
                  <ChevronDown className="absolute right-2 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400 pointer-events-none" />
                </div>
                <span className="text-sm text-gray-400">{totalTx} transactions</span>
              </div>

              <div className="bg-white rounded-xl border border-gray-200 overflow-hidden">
                {loading ? (
                  <div className="flex items-center justify-center py-16"><RefreshCw className="w-5 h-5 animate-spin text-gray-400" /></div>
                ) : transactions.length === 0 ? (
                  <div className="flex flex-col items-center justify-center py-16 text-gray-400">
                    <CreditCard className="w-8 h-8 mb-2" />
                    <p className="text-sm">No transactions found</p>
                  </div>
                ) : (
                  <div className="overflow-x-auto">
                    <table className="w-full text-sm">
                      <thead>
                        <tr className="border-b border-gray-100 bg-gray-50/50">
                          <th className="text-left px-4 py-3 font-medium text-gray-500">Company</th>
                          <th className="text-left px-4 py-3 font-medium text-gray-500">Plan</th>
                          <th className="text-right px-4 py-3 font-medium text-gray-500">Amount</th>
                          <th className="text-left px-4 py-3 font-medium text-gray-500">Status</th>
                          <th className="text-left px-4 py-3 font-medium text-gray-500">Date</th>
                        </tr>
                      </thead>
                      <tbody className="divide-y divide-gray-50">
                        {transactions.map(tx => (
                          <tr key={tx.id} className="hover:bg-gray-50/50">
                            <td className="px-4 py-3 font-medium text-gray-900">{tx.company_name || '—'}</td>
                            <td className="px-4 py-3 text-gray-600">{tx.plan_name || '—'}</td>
                            <td className="px-4 py-3 text-right font-medium text-gray-900">
                              ${typeof tx.amount === 'number' ? tx.amount.toFixed(2) : tx.amount}
                            </td>
                            <td className="px-4 py-3"><PaymentBadge status={tx.payment_status} /></td>
                            <td className="px-4 py-3 text-xs text-gray-400">{formatDate(tx.created_at)}</td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                )}
              </div>
            </>
          )}

          {/* Failed Payments Tab */}
          {tab === 'failed' && (
            <div className="bg-white rounded-xl border border-gray-200 overflow-hidden">
              {loading ? (
                <div className="flex items-center justify-center py-16"><RefreshCw className="w-5 h-5 animate-spin text-gray-400" /></div>
              ) : failedCompanies.length === 0 ? (
                <div className="flex flex-col items-center justify-center py-16 text-gray-400">
                  <CheckCircle className="w-8 h-8 mb-2 text-green-400" />
                  <p className="text-sm font-medium text-green-600">No failed payments</p>
                  <p className="text-xs text-gray-400 mt-1">All companies are in good standing</p>
                </div>
              ) : (
                <div className="divide-y divide-gray-100">
                  {failedCompanies.map(company => (
                    <div key={company.id} className="p-5 hover:bg-gray-50/50 transition">
                      <div className="flex items-start justify-between">
                        <div className="flex-1 min-w-0">
                          <div className="flex items-center gap-2 mb-1">
                            <h3 className="font-semibold text-gray-900">{company.name}</h3>
                            <span className={`inline-flex px-2 py-0.5 rounded-full text-xs font-medium ${
                              company.payment_retry_count >= 3 ? 'bg-red-100 text-red-700' :
                              company.payment_retry_count >= 2 ? 'bg-amber-100 text-amber-700' :
                              'bg-yellow-50 text-yellow-700'
                            }`}>
                              {company.payment_retry_count} retries
                            </span>
                            {!company.is_paid && <span className="px-2 py-0.5 bg-red-50 text-red-600 rounded-full text-xs font-medium">Unpaid</span>}
                          </div>

                          <div className="flex items-center gap-4 text-sm text-gray-500 mb-2">
                            <span>{company.plan_name || 'No plan'} — ${company.plan_price}/mo</span>
                            {company.cc_last4 && <span>Card: •••• {company.cc_last4}</span>}
                            {company.contact_email && <span>{company.contact_email}</span>}
                          </div>

                          {company.last_payment_error && (
                            <p className="text-xs text-red-500 mb-2">
                              <AlertTriangle className="w-3 h-3 inline mr-1" />
                              {company.last_payment_error}
                            </p>
                          )}

                          {company.grace_period_ends && (
                            <p className="text-xs text-amber-600">
                              <Clock className="w-3 h-3 inline mr-1" />
                              Grace period ends: {formatDate(company.grace_period_ends)}
                            </p>
                          )}
                        </div>

                        {/* Actions */}
                        <div className="flex items-center gap-2 flex-shrink-0 ml-4">
                          <button
                            onClick={() => handleRetry(company.id)}
                            disabled={actionLoading === company.id + '_retry'}
                            className="flex items-center gap-1 px-3 py-1.5 bg-blue-600 text-white rounded-lg text-xs font-medium hover:bg-blue-700 disabled:opacity-50"
                            title="Retry payment via Stripe"
                          >
                            <RotateCcw className="w-3.5 h-3.5" />
                            {actionLoading === company.id + '_retry' ? 'Retrying...' : 'Retry'}
                          </button>
                          <button
                            onClick={() => handleSendReminder(company.id)}
                            disabled={actionLoading === company.id + '_remind'}
                            className="flex items-center gap-1 px-3 py-1.5 bg-amber-500 text-white rounded-lg text-xs font-medium hover:bg-amber-600 disabled:opacity-50"
                            title="Send payment reminder email"
                          >
                            <Send className="w-3.5 h-3.5" />
                            {actionLoading === company.id + '_remind' ? 'Sending...' : 'Remind'}
                          </button>
                          <button
                            onClick={() => handleExtendGrace(company.id)}
                            disabled={actionLoading === company.id + '_extend'}
                            className="flex items-center gap-1 px-3 py-1.5 border border-gray-300 text-gray-700 rounded-lg text-xs font-medium hover:bg-gray-50 disabled:opacity-50"
                            title="Extend grace period by 7 days"
                          >
                            <CalendarPlus className="w-3.5 h-3.5" />
                            {actionLoading === company.id + '_extend' ? '...' : '+7 days'}
                          </button>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
          )}

        </div>
      </div>
    </Sidebar>
  );
};

export default AdminFinancialPayments;
